import { SOCIAL_PROVIDER } from "@artha/types";

/**
 * Formats a number to compact social media style (1K, 100K, 1M, etc.)
 * @param count The number to format
 * @returns Formatted string like "1K", "100K", "1.2M"
 */
export function formatCompactNumber(count: number | null | undefined): string {
  if (count === null || count === undefined || count === 0) {
    return "0";
  }

  if (count < 1000) {
    return count % 1 === 0 ? count.toString() : count.toFixed(2);
  }

  if (count < 1000000) {
    const k = Math.floor(count / 100) / 10;
    return k % 1 === 0 ? `${Math.floor(k)}K` : `${k}K`;
  }

  if (count < 1000000000) {
    const m = Math.floor(count / 100000) / 10;
    return m % 1 === 0 ? `${Math.floor(m)}M` : `${m}M`;
  }

  const b = Math.floor(count / 100000000) / 10;
  return b % 1 === 0 ? `${Math.floor(b)}B` : `${b}B`;
}

/**
 * Formats follower count to social media style (1K, 100K, 1M, etc.)
 * @param count The follower count number
 * @returns Formatted string like "1K", "100K", "1.2M"
 * @deprecated Use formatCompactNumber instead
 */
export function formatFollowerCount(count: number | null | undefined): string {
  return formatCompactNumber(count);
}

/**
 * Removes trailing slash(es) from a pathname or URL string
 * @param pathname The string potentially ending with slashes
 * @returns String without trailing slashes
 */
function stripTrailingSlash(pathname: string): string {
  return pathname.replace(/\/+$/, "");
}

/**
 * Removes a leading "www." (case-insensitive) from a hostname
 * @param hostname The hostname string to clean
 * @returns Hostname without leading "www."
 */
function removeLeadingWww(hostname: string): string {
  return hostname.replace(/^www\./i, "");
}

/**
 * Extracts a Substack handle from a pathname if it follows the "/@handle" pattern
 * @param pathname The URL pathname
 * @returns The extracted handle or null if not found
 */
function extractSubstackHandleFromPath(pathname: string): string | null {
  const match = stripTrailingSlash(pathname).match(/^\/@([^/]+)$/i);
  return match ? match[1] : null;
}

/**
 * Normalizes a Substack input string (username or URL) into a standard Substack profile URL
 * @param input Username, handle, or URL input
 * @returns Normalized Substack URL
 */
function normalizeSubstackUrl(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return trimmed;

  const cleanInput = trimmed.replace(/^\/+/, "");
  const isBareHandle =
    (cleanInput.startsWith("@") && !cleanInput.includes("/")) ||
    (!cleanInput.includes(".") && !cleanInput.includes("/"));

  if (isBareHandle) {
    return `https://${cleanInput.replace(/^@+/, "")}.substack.com`;
  }

  const withProtocol =
    trimmed.startsWith("http://") || trimmed.startsWith("https://")
      ? trimmed
      : `https://${cleanInput}`;

  try {
    const urlObj = new URL(withProtocol);
    const hostname = removeLeadingWww(urlObj.hostname.toLowerCase());
    const pathname = stripTrailingSlash(urlObj.pathname);

    if (hostname === "substack.com") {
      const handle = extractSubstackHandleFromPath(pathname);
      if (handle) {
        return `https://${handle}.substack.com`;
      }

      urlObj.hostname = hostname;
      urlObj.search = "";
      urlObj.hash = "";
      return urlObj.toString().replace(/\/$/, "");
    }

    urlObj.hostname = hostname;
    urlObj.search = "";
    urlObj.hash = "";
    return urlObj.toString().replace(/\/$/, "");
  } catch {
    return trimmed;
  }
}

/**
 * Validates a Substack URL and returns an error message if it's invalid
 * @param url The normalized Substack URL to check
 * @returns Validation error message or null if valid
 */
function getSubstackValidationError(url: string): string | null {
  try {
    const urlObj = new URL(url);
    const pathname = stripTrailingSlash(urlObj.pathname);

    if (!pathname) {
      return null;
    }

    if (pathname.startsWith("/p/")) {
      return "Please enter your Substack publication home URL, not a specific post URL.";
    }

    if (
      removeLeadingWww(urlObj.hostname).toLowerCase() === "substack.com" &&
      extractSubstackHandleFromPath(pathname)
    ) {
      return null;
    }

    return "Please enter your Substack publication home URL.";
  } catch {
    return "Invalid URL format";
  }
}

/**
 * Extracts username/handle from social media profile URLs
 * @param provider The social media platform
 * @param url The full profile URL
 * @returns Extracted username/handle
 */
export function extractHandleFromUrl(provider: string, url: string): string {
  if (!url) return "";

  const trimmed = url.trim();

  switch (provider.toLowerCase()) {
    case SOCIAL_PROVIDER.LINKEDIN:
      // Extract from LinkedIn URLs like https://www.linkedin.com/in/username
      const linkedinMatch = trimmed.match(/linkedin\.com\/in\/([^/?]+)/i);
      return linkedinMatch ? linkedinMatch[1] : "";

    case SOCIAL_PROVIDER.INSTAGRAM:
      // Extract from Instagram URLs like https://www.instagram.com/username
      const instagramMatch = trimmed.match(/instagram\.com\/([^/?]+)/i);
      return instagramMatch ? instagramMatch[1] : "";

    case SOCIAL_PROVIDER.TIKTOK:
      // Extract from TikTok URLs like https://www.tiktok.com/@username
      const tiktokMatch = trimmed.match(/tiktok\.com\/@([^/?]+)/i);
      return tiktokMatch ? tiktokMatch[1] : "";

    case SOCIAL_PROVIDER.YOUTUBE:
      // Extract from YouTube URLs - various formats
      const youtubeChannelMatch = trimmed.match(/youtube\.com\/c\/([^/?]+)/i);
      if (youtubeChannelMatch) return youtubeChannelMatch[1];

      const youtubeHandleMatch = trimmed.match(/youtube\.com\/@([^/?]+)/i);
      if (youtubeHandleMatch) return `@${youtubeHandleMatch[1]}`;

      const youtubeUserMatch = trimmed.match(/youtube\.com\/user\/([^/?]+)/i);
      if (youtubeUserMatch) return youtubeUserMatch[1];

      const youtubeChannelIdMatch = trimmed.match(/youtube\.com\/channel\/([^/?]+)/i);
      if (youtubeChannelIdMatch) return youtubeChannelIdMatch[1];

      return "";

    case SOCIAL_PROVIDER.SUBSTACK:
      // Extract from Substack URLs like https://username.substack.com
      const substackProfileMatch = trimmed.match(/substack\.com\/@([^/?]+)/i);
      if (substackProfileMatch) return substackProfileMatch[1];

      const substackMatch = trimmed.match(/https?:\/\/(?:www\.)?([^.]+)\.substack\.com/i);
      if (substackMatch) return substackMatch[1];

      try {
        const hostname = removeLeadingWww(new URL(trimmed).hostname);
        return hostname || "";
      } catch {
        return "";
      }

    case SOCIAL_PROVIDER.MEDIUM:
      // Extract from Medium URLs like https://medium.com/@username
      const mediumMatch = trimmed.match(/medium\.com\/@([^/?]+)/i);
      return mediumMatch ? mediumMatch[1] : "";

    default:
      // For unknown providers, try to extract the last path segment
      try {
        const urlObj = new URL(trimmed);
        const pathSegments = urlObj.pathname.split('/').filter(Boolean);
        return pathSegments[pathSegments.length - 1] || "";
      } catch {
        return "";
      }
  }
}

/**
 * Normalizes social media URLs and usernames to proper profile URLs
 * @param provider The social media platform
 * @param input Username or URL input from user
 * @returns Normalized profile URL
 */
export function normalizeSocialUrl(provider: string, input: string): string {
  const trimmed = input.trim();
  const providerLower = provider.toLowerCase();

  if (providerLower === SOCIAL_PROVIDER.SUBSTACK) {
    return normalizeSubstackUrl(trimmed);
  }

  // If it's already a full URL, clean it up by removing query params and hash
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const urlObj = new URL(trimmed);
      // Strip query parameters and hash for social profile URLs
      if (providerLower !== SOCIAL_PROVIDER.WEBSITE) {
        urlObj.search = '';
        urlObj.hash = '';
      }
      // Remove trailing slash for consistency
      return urlObj.toString().replace(/\/$/, '');
    } catch {
      return trimmed;
    }
  }

  // Remove leading / if present, but preserve @ for some platforms
  const cleanInput = trimmed.replace(/^\/+/, '');

  switch (providerLower) {
    case SOCIAL_PROVIDER.LINKEDIN:
      // Handle LinkedIn profile URLs without protocol
      if (cleanInput.includes('linkedin.com/')) {
        return normalizeSocialUrl(provider, `https://${cleanInput}`);
      }
      // Remove @ for LinkedIn
      const linkedinUsername = cleanInput.replace(/^@+/, '');
      return `https://www.linkedin.com/in/${linkedinUsername}`;

    case SOCIAL_PROVIDER.INSTAGRAM:
      // Handle Instagram URLs without protocol
      if (cleanInput.includes('instagram.com/')) {
        return normalizeSocialUrl(provider, `https://${cleanInput}`);
      }
      // Remove @ for Instagram
      const instagramUsername = cleanInput.replace(/^@+/, '');
      return `https://www.instagram.com/${instagramUsername}`;

    case SOCIAL_PROVIDER.TIKTOK:
      // Handle TikTok URLs without protocol
      if (cleanInput.includes('tiktok.com/')) {
        return normalizeSocialUrl(provider, `https://${cleanInput}`);
      }
      // Remove @ for TikTok (we'll add it back)
      const tiktokUsername = cleanInput.replace(/^@+/, '');
      return `https://www.tiktok.com/@${tiktokUsername}`;

    case SOCIAL_PROVIDER.YOUTUBE:
      // Handle YouTube URLs without protocol
      if (cleanInput.includes('youtube.com/') || cleanInput.includes('youtu.be/')) {
        return normalizeSocialUrl(provider, `https://${cleanInput}`);
      }
      // If it starts with @, it's a handle
      if (cleanInput.startsWith('@')) {
        return `https://www.youtube.com/${cleanInput}`;
      }
      // Otherwise assume it's a channel ID or custom URL
      const youtubeUsername = cleanInput.replace(/^@+/, '');
      return `https://www.youtube.com/c/${youtubeUsername}`;

    case SOCIAL_PROVIDER.MEDIUM:
      // Handle Medium URLs without protocol
      if (cleanInput.includes('medium.com/')) {
        return normalizeSocialUrl(provider, `https://${cleanInput}`);
      }
      // Remove @ for Medium (we'll add it back)
      const mediumUsername = cleanInput.replace(/^@+/, '');
      return `https://medium.com/@${mediumUsername}`;

    case SOCIAL_PROVIDER.WEBSITE:
      // For websites, ensure it has a protocol
      if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
        return normalizeSocialUrl(provider, `https://${cleanInput}`);
      }
      return trimmed;

    default:
      // Fallback: return as-is or add https if it looks like a domain
      if (cleanInput.includes('.') && !trimmed.startsWith('http')) {
        return normalizeSocialUrl(provider, `https://${cleanInput}`);
      }
      return trimmed;
  }
}
/**
 * Detects social media platform from URL
 * @param url The profile URL to analyze
 * @returns Platform name or 'unknown' if not recognized
 */
export function detectPlatformFromUrl(url: string): string {
  if (!url) return 'unknown';

  const normalizedUrl = url.toLowerCase();

  if (normalizedUrl.includes('tiktok.com')) return 'tiktok';
  if (normalizedUrl.includes('linkedin.com')) return 'linkedin';
  if (normalizedUrl.includes('instagram.com')) return 'instagram';
  if (normalizedUrl.includes('youtube.com') || normalizedUrl.includes('youtu.be')) return 'youtube';
  if (normalizedUrl.includes('substack.com')) return 'substack';
  if (normalizedUrl.includes('medium.com')) return 'medium';

  return 'unknown';
}

/**
 * URL pattern configurations for each social platform
 * Used for validation to ensure URLs match the selected provider
 */
const SOCIAL_URL_PATTERNS: Record<string, RegExp[]> = {
  linkedin: [
    /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9_-]+\/?$/i,
    /^https?:\/\/(www\.)?linkedin\.com\/company\/[a-zA-Z0-9_-]+\/?$/i,
  ],
  instagram: [
    /^https?:\/\/(www\.)?instagram\.com\/[a-zA-Z0-9_.]+\/?$/i,
  ],
  tiktok: [
    /^https?:\/\/(www\.)?tiktok\.com\/@[a-zA-Z0-9_.]+\/?$/i,
  ],
  youtube: [
    /^https?:\/\/(www\.)?youtube\.com\/@[a-zA-Z0-9_.+-]+\/?$/i,
    /^https?:\/\/(www\.)?youtube\.com\/c\/[a-zA-Z0-9_.+-]+\/?$/i,
    /^https?:\/\/(www\.)?youtube\.com\/channel\/[a-zA-Z0-9_.+-]+\/?$/i,
    /^https?:\/\/(www\.)?youtube\.com\/user\/[a-zA-Z0-9_.+-]+\/?$/i,
  ],
  substack: [
    /^https?:\/\/substack\.com\/@[a-zA-Z0-9_-]+\/?$/i,
    /^https?:\/\/[a-zA-Z0-9_-]+\.substack\.com\/?$/i,
    /^https?:\/\/(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\/?$/i,
  ],
  medium: [
    /^https?:\/\/(www\.)?medium\.com\/@[a-zA-Z0-9_.-]+\/?$/i,
  ],
  website: [
    /^https?:\/\/.+\..+/i, // Any valid URL with domain
  ],
};

/**
 * Validates if a URL matches the expected format for a given social provider
 * @param provider The social media platform
 * @param url The URL to validate
 * @returns true if URL matches provider's expected format
 */
export function isValidSocialUrl(provider: string, url: string): boolean {
  if (!url || !provider) return false;

  const patterns = SOCIAL_URL_PATTERNS[provider.toLowerCase()];

  // For unknown providers or website, just check if it's a valid URL
  if (!patterns) {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  return patterns.some(pattern => pattern.test(url));
}

/**
 * Gets the expected URL format example for a given provider
 * @param provider The social media platform
 * @returns Example URL format string
 */
export function getSocialUrlExample(provider: string): string {
  const examples: Record<string, string> = {
    linkedin: 'https://linkedin.com/in/username',
    instagram: 'https://instagram.com/username',
    tiktok: 'https://tiktok.com/@username',
    youtube: 'https://youtube.com/@username',
    substack: 'https://username.substack.com',
    medium: 'https://medium.com/@username',
    website: 'https://example.com',
  };

  return examples[provider.toLowerCase()] || 'https://example.com';
}

/**
 * Validates URL matches the selected provider
 * Returns error message if invalid, null if valid
 * @param provider The social media platform
 * @param url The URL to validate (can be username or full URL)
 * @returns Error message or null if valid
 */
export function validateSocialProfileUrl(provider: string, url: string): string | null {
  if (!url || !url.trim()) {
    return 'URL is required';
  }

  // First normalize the URL
  const normalizedUrl = normalizeSocialUrl(provider, url);

  // Check if it's a valid URL format
  try {
    new URL(normalizedUrl);
  } catch {
    return 'Invalid URL format';
  }

  const providerLower = provider.toLowerCase();

  // For website provider, any valid URL is acceptable
  if (providerLower === SOCIAL_PROVIDER.WEBSITE) {
    return null;
  }

  // Detect what platform the URL actually belongs to
  const detectedPlatform = detectPlatformFromUrl(normalizedUrl);

  // If we detected a platform and it doesn't match, return error
  if (detectedPlatform !== 'unknown' && detectedPlatform !== providerLower) {
    return `This URL appears to be a ${detectedPlatform} profile, not ${provider}`;
  }

  if (providerLower === SOCIAL_PROVIDER.SUBSTACK) {
    const substackError = getSubstackValidationError(normalizedUrl);
    if (substackError) {
      return substackError;
    }
  }

  // Validate URL matches expected pattern for the provider
  if (!isValidSocialUrl(provider, normalizedUrl)) {
    const example = getSocialUrlExample(provider);
    return `Invalid ${provider} profile URL. Expected format: ${example}`;
  }

  return null;
}

/**
 * Click conversion rate tiers based on follower count
 * Higher follower counts have lower engagement rates
 */
const CLICK_CONVERSION_TIERS = [
  { maxFollowers: 10_000, rate: 0.01 },    // 1.0%
  { maxFollowers: 50_000, rate: 0.006 },   // 0.6%
  { maxFollowers: Infinity, rate: 0.005 }, // 0.5%
] as const;

/** Application conversion rate from clicks */
const APPLICATION_CONVERSION_RATE = 0.10; // 10%

/** Default average CPA */
const DEFAULT_AVG_CPA = 15;

/** Weeks per month for calculations */
const WEEKS_PER_MONTH = 4;

/**
 * Gets the click conversion rate based on follower count
 * @param followers The number of followers
 * @returns Click conversion rate (0-1)
 */
export function getClickConversionRate(followers: number): number {
  for (const tier of CLICK_CONVERSION_TIERS) {
    if (followers <= tier.maxFollowers) {
      return tier.rate;
    }
  }
  return CLICK_CONVERSION_TIERS[CLICK_CONVERSION_TIERS.length - 1].rate;
}

/**
 * Calculates weekly clicks based on follower count
 * Formula: Followers × Click Conversion Rate
 * @param followers The number of followers
 * @returns Estimated weekly clicks
 */
export function calculateWeeklyClicks(followers: number): number {
  if (followers <= 0) return 0;
  const conversionRate = getClickConversionRate(followers);
  return Math.round(followers * conversionRate);
}

/**
 * Calculates weekly applications from clicks
 * Formula: Weekly Clicks × 10% conversion
 * @param weeklyClicks Number of weekly clicks
 * @returns Estimated weekly applications
 */
export function calculateWeeklyApplications(weeklyClicks: number): number {
  return Math.round(weeklyClicks * APPLICATION_CONVERSION_RATE);
}

/**
 * Calculates weekly revenue based on applications and CPA
 * Formula: Weekly Applications × AVG CPA
 * @param weeklyApplications Number of weekly applications
 * @param avgCpa Average Cost Per Application (defaults to $15)
 * @returns Weekly revenue estimate
 */
export function calculateWeeklyRevenue(weeklyApplications: number, avgCpa: number = DEFAULT_AVG_CPA): number {
  return weeklyApplications * avgCpa;
}

/**
 * Calculates complete earning potential for a creator
 * @param followers The number of followers
 * @param avgCpa Average Cost Per Application (defaults to $15)
 * @returns Complete earning potential breakdown
 */
export function calculateEarningPotential(
  followers: number,
  avgCpa: number = DEFAULT_AVG_CPA
): {
  weeklyClicks: number;
  weeklyApplications: number;
  weeklyRevenue: number;
  tiers: Array<{ frequency: string; weeklyAmount: number; monthlyAmount: number }>;
  monthlyEstimate: number;
} {
  const weeklyClicks = calculateWeeklyClicks(followers);
  const weeklyApplications = calculateWeeklyApplications(weeklyClicks);
  const weeklyRevenue = calculateWeeklyRevenue(weeklyApplications, avgCpa);

  // Calculate tiers based on sharing frequency
  const tiers = [
    {
      frequency: "Post 2x/week",
      weeklyAmount: weeklyRevenue * 2,
      monthlyAmount: weeklyRevenue * 2 * WEEKS_PER_MONTH,
    },
    {
      frequency: "Post 3x/week",
      weeklyAmount: weeklyRevenue * 3,
      monthlyAmount: weeklyRevenue * 3 * WEEKS_PER_MONTH,
    },
  ];

  // Monthly estimate is sum of all tier monthly amounts
  const monthlyEstimate = tiers.reduce((sum, tier) => sum + tier.monthlyAmount, 0);

  return {
    weeklyClicks,
    weeklyApplications,
    weeklyRevenue,
    tiers,
    monthlyEstimate,
  };
}
